import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Globe, Layers, BarChart3, Shuffle, Sparkles } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyServices } from '@/api/services';
import { useProviders } from '@/api/service-provisioning';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { useSelfServeActivation } from '../activation-mode';
import { ServiceContactSalesDialog } from '../components/ServiceContactSalesDialog';
import { CdnActivateDialog } from './CdnActivateDialog';
import { CDN_PROVIDER, CDN_PRODUCT, cdnConsoleRoute } from './cdn-activate';
// Reuse the Object Storage onboarding hero visuals (shared --vn-* token styles);
// cross-service style import mirrors how the WAAP landing borrows its siblings'.
import { OnboardingCss } from '../oss/onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from '../oss/onboarding-styles';

/**
 * Multi-CDN activation landing — the first-run screen for an org with no CDN
 * orchestrator. Mirrors the Compute/Kubernetes/Storage landings: staff
 * impersonation provisions in-app (the vEdge `raven` provider, via
 * CdnActivateDialog), while a direct customer sends a contact-sales inquiry. An
 * org that already has an orchestrator is sent straight into its console.
 */
export function CdnActivateLanding({ onBack }: { onBack?: () => void }) {
  const { data: services = [], isLoading } = useMyServices();
  const { data: providers = [], isLoading: providersLoading } = useProviders();
  // Self-serve provisioning is gated (contact-sales for direct customers, on
  // while staff impersonate); when off the CTA opens a contact-sales dialog.
  const [activateOpen, setActivateOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const selfServeActivation = useSelfServeActivation();
  const onActivate = () => (selfServeActivation ? setActivateOpen(true) : setContactOpen(true));

  const existing = useMemo(
    () => services.find((s) => s.provider?.name?.trim().toLowerCase() === CDN_PROVIDER),
    [services],
  );
  const available = useMemo(
    () =>
      providers.some((p) => p.name?.trim().toLowerCase() === CDN_PROVIDER && p.status === 'active'),
    [providers],
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <Header onBack={onBack} />
        <Skeleton style={{ height: 200 }} />
      </Container>
    );
  }

  if (existing) {
    return <Navigate to={cdnConsoleRoute(existing.id)} replace />;
  }

  const unavailable = !providersLoading && !available;

  return (
    <Container size="xl">
      <Header onBack={onBack} />

      {unavailable ? (
        <Alert tone="warning" title={`${CDN_PRODUCT.displayName} is not available yet`}>
          {CDN_PRODUCT.displayName} is not available for self-serve activation on your account yet.
          Please contact our team to enable it.
        </Alert>
      ) : (
        <div className="vn-ob-hero" style={heroWrap}>
          <OnboardingCss />

          <span className="vn-ob-halo" style={heroIcon} aria-hidden>
            <Globe size={30} />
          </span>

          <p style={heroEyebrow}>{CDN_PRODUCT.displayName}</p>
          <h2 style={heroTitle}>Activate {CDN_PRODUCT.displayName} for your organization</h2>
          <p style={heroLede}>{CDN_PRODUCT.description}</p>

          <Button
            size="lg"
            startIcon={<Sparkles size={16} />}
            onClick={onActivate}
            style={{ marginTop: 'var(--vn-space-2)' }}
          >
            Activate {CDN_PRODUCT.displayName}
          </Button>

          <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
            {PERKS.map((p) => (
              <div key={p.title} style={stepRow}>
                <span style={markerAccent}>{p.icon}</span>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={stepTitle}>{p.title}</p>
                  <p style={stepDesc}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CdnActivateDialog open={activateOpen} onOpenChange={setActivateOpen} />
      <ServiceContactSalesDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        serviceLabel={CDN_PRODUCT.displayName}
        productGroup={PRODUCT_GROUP.cdn}
      />
    </Container>
  );
}

function Header({ onBack }: { onBack?: () => void }) {
  return (
    <PageHeader
      icon={<Globe size={22} />}
      title="Multi-CDN Orchestration"
      description="Deliver your domains across multiple CDN backends from one console, with unified analytics and failover."
      actions={
        onBack ? (
          <Button variant="ghost" onClick={onBack}>
            Back to services
          </Button>
        ) : undefined
      }
    />
  );
}

const PERKS = [
  {
    icon: <Layers size={16} />,
    title: 'Multi-backend delivery',
    desc: 'Route traffic across multiple CDN providers from a single orchestrator.',
  },
  {
    icon: <BarChart3 size={16} />,
    title: 'Unified analytics',
    desc: 'One dashboard for traffic, cache and performance across every backend.',
  },
  {
    icon: <Shuffle size={16} />,
    title: 'Origin failover',
    desc: 'Automatic failover keeps content served when a backend degrades.',
  },
];

export default CdnActivateLanding;
